import { X } from 'lucide-react'
import * as React from 'react'

import { cn } from '../../utils/cn'
import { Button } from './button'
import { IndeterminateLoadingBar } from './IndeterminateLoadingBar'

type FileChipProps = React.ComponentProps<'span'> & {
  name: string
  /** What is known about the file, such as "2 pages"; omitted while it uploads. */
  detail?: string
  /** Shows "Uploading…" with a bar along the chip's foot. */
  uploading?: boolean
  /** Adds an X that calls this. */
  onRemove?: () => void
  /** The X's request is in flight. */
  removing?: boolean
}

/** A file by name, as a chip: in a composer before sending, on a sent message, or in a list of files. */
function FileChip({
  name,
  detail,
  uploading = false,
  onRemove,
  removing = false,
  className,
  ...props
}: FileChipProps) {
  return (
    <span
      data-slot="file-chip"
      className={cn(
        'bg-muted text-foreground relative inline-flex h-7 max-w-full min-w-0 items-center gap-1 overflow-hidden rounded-sm px-2 text-xs font-medium whitespace-nowrap has-data-[slot=file-chip-remove]:pr-0.5',
        className
      )}
      {...props}
    >
      <span className="min-w-0 truncate">{name}</span>
      {detail && <span className="text-ink-500 shrink-0">{detail}</span>}
      {uploading && (
        <>
          <span className="text-ink-500 shrink-0" role="status">
            Uploading…
          </span>
          <IndeterminateLoadingBar className="absolute inset-x-0 bottom-0" />
        </>
      )}
      {onRemove && (
        <Button
          type="button"
          variant="ghost"
          size="icon-sm"
          className="size-6 shrink-0 opacity-50 hover:opacity-100"
          data-slot="file-chip-remove"
          aria-label={`Remove ${name}`}
          loading={removing}
          onClick={onRemove}
        >
          <X aria-hidden="true" />
        </Button>
      )}
    </span>
  )
}

export { FileChip }
